import LogoUndiksha from "@/assets/logos/undiksha.png";
import { Button } from "@/components/ui/button";
import useTimePeriode from "@/hooks/useTimePeriode";
import { getLinimasaUnregular } from "@/services/getData";
import { Periode } from "@/types";
import { useQuery } from "@tanstack/react-query";
import { ArrowDown, ChevronRight, FileText, GraduationCap } from "lucide-react";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const TimelineRow = ({
  icon: Icon,
  label,
  date,
  isLast = false,
}: {
  icon: any;
  label: string;
  date: string;
  isLast?: boolean;
}) => (
  <div className="relative flex gap-4 pb-6 last:pb-0">
    {/* Garis vertikal timeline */}
    {!isLast && (
      <span className="absolute left-[11px] top-8 h-full w-[2px] bg-slate-100 dark:bg-slate-800" />
    )}

    {/* Icon Wrapper */}
    <div className="relative z-10 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-50 dark:bg-blue-900/30 ring-4 ring-white dark:ring-slate-900">
      <Icon className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400" />
    </div>

    {/* Content */}
    <div className="flex flex-col gap-1">
      <span className="text-sm font-medium text-slate-900 dark:text-slate-200 leading-none">
        {label}
      </span>
      <span className="text-sm text-slate-500 dark:text-slate-400">{date}</span>
    </div>
  </div>
);

const StudentExchangePage: React.FC = () => {
  const { dataPeriode } = useTimePeriode();
  const { i18n } = useTranslation();
  const navigate = useNavigate();

  const { data: linimasaUnregular } = useQuery<Periode[]>({
    queryKey: ["linimasa-unregular-exchange"],
    queryFn: () => getLinimasaUnregular("studentexchange"),
  });

  const handleNavigate = (path: string) => {
    // Automatically switch to English for international/exchange students
    i18n.changeLanguage("en");
    localStorage.setItem("lang", "en");
    navigate(path);
  };

  const formatDateToIndonesian = (date: string | Date | undefined) => {
    if (!date) return "-";
    const d = new Date(date);
    if (isNaN(d.getTime())) return "-";
    return new Intl.DateTimeFormat("en-US", {
      day: "numeric",
      month: "long",
      year: "numeric",
    }).format(d);
  };

  const formatDateRange = (start?: string, end?: string) => {
    if (!start) return "-";
    const startDate = formatDateToIndonesian(start);
    if (!end) return startDate;
    const endDate = formatDateToIndonesian(end);
    if (startDate === endDate) return startDate;
    return `${startDate} - ${endDate}`;
  };

  const getSchedules = (item: Periode) => {
    return [
      {
        icon: FileText,
        label: "Application Period",
        date: formatDateRange(
          item.waktu_mulai_daftar,
          item.waktu_selesai_daftar,
        ),
      },
    ];
  };

  const activePeriods = useMemo(() => {
    if (linimasaUnregular && linimasaUnregular.length > 0) {
      return linimasaUnregular;
    }
    return dataPeriode ? [dataPeriode] : [];
  }, [linimasaUnregular, dataPeriode]);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 font-sans selection:bg-primary/20 relative overflow-x-hidden">
      {/* Background Decorative Elements */}
      <div className="fixed inset-0 -z-10 opacity-20 pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] bg-primary rounded-full blur-[120px] animate-pulse" />
        <div
          className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] bg-indigo-400 rounded-full blur-[120px] animate-pulse"
          style={{ animationDelay: "2s" }}
        />
      </div>

      <div className="w-full">
        {/* Hero Section */}
        <div className="min-h-screen flex flex-col items-center justify-center px-4 text-center">
          {/* Logo/Icon */}
          <div className="inline-flex items-center justify-center w-24 h-24 mb-8 animate-bounce-subtle">
            <img
              src={LogoUndiksha}
              alt="Undiksha Logo"
              className="w-full h-full object-contain"
            />
          </div>

          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 mb-8 animate-fade-in">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
            </span>
            <span className="text-sm font-semibold text-primary uppercase tracking-wider">
              Student Exchange {dataPeriode?.tahun}
            </span>
          </div>

          <h1 className="text-5xl md:text-7xl font-extrabold text-slate-900 dark:text-white tracking-tight mb-6 leading-tight">
            Student Exchange <br />
            <span className="bg-gradient-to-r from-primary to-indigo-600 bg-clip-text text-transparent">
              Program
            </span>
          </h1>

          <p className="max-w-2xl mx-auto text-xl text-slate-600 dark:text-slate-400 mb-12 leading-relaxed font-medium">
            Explore new cultures and academic horizons. Join our exchange
            program at Ganesha University of Education, Bali.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-5">
            <Button
              size="lg"
              onClick={() => handleNavigate("/registrasi?tag=student")}
              className="w-full sm:w-auto h-16 px-10 text-lg bg-primary hover:bg-primary/90 text-primary-foreground rounded-2xl shadow-xl shadow-primary/25 group transition-all hover:scale-105 active:scale-95 border-none"
            >
              Create Account
              <ChevronRight className="ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>

            <Button
              size="lg"
              variant="outline"
              onClick={() => handleNavigate("/login?tag=student")}
              className="w-full sm:w-auto h-16 px-10 text-lg border-2 rounded-2xl bg-white/50 dark:bg-slate-900/50 backdrop-blur-sm border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-200 hover:bg-white dark:hover:bg-slate-800 transition-all hover:scale-105 active:scale-95"
            >
              Sign In
            </Button>
          </div>

          <Button
            size="lg"
            variant="ghost"
            onClick={() => {
              document
                .getElementById("program-timeline")
                ?.scrollIntoView({ behavior: "smooth" });
            }}
            className="w-full sm:w-auto h-16 px-10 text-lg rounded-2xl text-slate-500 hover:text-primary hover:bg-primary/5 transition-all mt-6"
          >
            <ArrowDown className="mr-2 w-5 h-5 animate-bounce" />
            View Programs
          </Button>
        </div>

        {/* Timeline Section */}
        <div
          id="program-timeline"
          className="max-w-4xl mx-auto px-4 pb-32 scroll-mt-20"
        >
          {activePeriods.length > 0 && (
            <div className="w-full space-y-10">
              <div className="flex flex-col items-center gap-2 text-center">
                <h2 className="text-3xl font-bold text-slate-800 dark:text-slate-200">
                  Open Programs{" "}
                  {linimasaUnregular && linimasaUnregular[0]?.tahun}
                </h2>
                <p className="text-slate-500 dark:text-slate-400 font-medium">
                  Available exchange opportunities for the current period
                </p>
              </div>

              <div
                className={`grid grid-cols-1 ${
                  activePeriods.length > 1
                    ? "md:grid-cols-2"
                    : "max-w-xl mx-auto"
                } gap-8 text-center`}
              >
                {activePeriods.map((period, pIdx) => (
                  <div
                    key={pIdx}
                    className="bg-white dark:bg-slate-900 rounded-3xl p-8 border border-slate-100 dark:border-slate-800 shadow-xl shadow-slate-200/50 dark:shadow-none flex flex-col h-full"
                  >
                    <div className="flex items-center justify-between mb-8 pb-4 border-b border-slate-50 dark:border-slate-800">
                      <div className="flex flex-col gap-1 text-left">
                        <span className="text-sm font-bold text-primary uppercase tracking-wider">
                          {period.nama_jalur_jenis || "Student Exchange"}
                        </span>
                        <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400 font-bold uppercase tracking-tighter">
                          <GraduationCap className="w-3.5 h-3.5" />
                          {Number(period.semester_masuk) === 1
                            ? "Odd Semester"
                            : "Even Semester"}{" "}
                          {period.tahun_ajaran_masuk}/
                          {Number(period.tahun_ajaran_masuk) + 1}
                        </div>
                      </div>
                      {period.gelombang_show && (
                        <div className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-bold border border-primary/20 whitespace-nowrap shrink-0">
                          Wave {period.gelombang_show}
                        </div>
                      )}
                    </div>

                    <div className="flex flex-col text-left flex-1">
                      {getSchedules(period).map((sch, sIdx) => (
                        <TimelineRow
                          key={sIdx}
                          icon={sch.icon}
                          label={sch.label}
                          date={sch.date}
                          isLast={true}
                        />
                      ))}

                      {period.sys_daftar_program &&
                        period.sys_daftar_program.length > 0 && (
                          <div className="mt-6 pt-6 border-t border-slate-50 dark:border-slate-800">
                            <h4 className="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-4">
                              Available Programs
                            </h4>
                            <div className="space-y-4">
                              {period.sys_daftar_program.map((prog, prIdx) => (
                                <div
                                  key={prIdx}
                                  className="flex items-start gap-3 group/prog bg-slate-50/50 dark:bg-slate-800/50 p-3 rounded-xl border border-transparent hover:border-primary/20 hover:bg-white dark:hover:bg-slate-800 transition-all"
                                >
                                  <div className="h-6 w-6 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                                    <div className="h-1.5 w-1.5 rounded-full bg-primary" />
                                  </div>
                                  <div className="flex flex-col gap-0.5">
                                    <span className="text-sm font-bold text-slate-700 dark:text-slate-200">
                                      {prog.ref_daftar_program?.nama_program}
                                    </span>
                                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-tight">
                                      Program Code:{" "}
                                      <span className="text-primary/70">
                                        {prog.ref_daftar_program?.kode_program}
                                      </span>
                                    </span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="max-w-4xl mx-auto px-4 pb-20">
          <div className="pt-8 border-t border-slate-200/50 dark:border-slate-800/50 text-center">
            <p className="text-slate-400 dark:text-slate-600 text-sm font-semibold tracking-wide uppercase">
              © {new Date().getFullYear()} Universitas Pendidikan Ganesha
            </p>
          </div>
        </div>
      </div>

      <style
        dangerouslySetInnerHTML={{
          __html: `
        @keyframes bounce-subtle {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        .animate-bounce-subtle {
          animation: bounce-subtle 3s ease-in-out infinite;
        }
      `,
        }}
      />
    </div>
  );
};

export default StudentExchangePage;
